import { inject, Injectable, signal, Signal } from '@angular/core';
import { StorageService } from '@/core/service/storage.service';
import { Action } from '@/core/util/permission-action';
import { IPermissionService } from '@/core/interface/permission-service.interface';
import { STORAGE_KEYS } from '@/share/constant/storage_token.key';
import { LoggerService } from '@/core/service/logger-service';

export interface MenuFunction {
    id: number;
    functionName: Action;
}

export interface MenuPermission {
    id: number;
    featureName: string;
    permissions: MenuFunction[];
}

@Injectable({ providedIn: 'root' })
export class PermissionService implements IPermissionService {
    // ✅ Index signature avoids TS7053
    private _permissions = signal<{ [key: string]: Set<Action> }>({});

    readonly permissions: Signal<{ [key: string]: Set<Action> }> = this._permissions.asReadonly();

    private _featureActions = signal<Set<Action>>(new Set());
    readonly featureActions: Signal<Set<Action>> = this._featureActions.asReadonly();

    private readonly logger = inject(LoggerService);

    constructor(private storageService: StorageService) {
        this.loadFromStorage();
    }

    loadPermissions(menus: MenuPermission[]) {
        const map: { [key: string]: Set<Action> } = {};
        menus.forEach((menu) => {
            map[menu.featureName.toUpperCase()] = new Set(menu.permissions.map((f) => f.functionName.toUpperCase() as Action));
        });
        this._permissions.set(map);
        this.logger.debug('permissions loaded', this.permissions());
        this.saveToStorage();
    }

    setCurrentFeature(feature: string) {
        const actions = this._permissions()[feature.toUpperCase()] ?? new Set<Action>();
        this._featureActions.set(actions);
    }

    private saveToStorage() {
        const obj: { [key: string]: string[] } = {};
        for (const key in this._permissions()) {
            obj[key] = Array.from(this._permissions()[key]);
        }
        this.storageService.set(STORAGE_KEYS.MENU_ITEM, obj);
    }

    private loadFromStorage() {
        const data = this.storageService.get(STORAGE_KEYS.MENU_ITEM);
        if (data !== null) {
            try {
                const obj = data as { [key: string]: string[] };
                const map: { [key: string]: Set<Action> } = {};
                for (const key in obj) {
                    map[key] = new Set(obj[key] as Action[]);
                }
                this._permissions.set(map);
            } catch (e) {
                this.logger.error('Failed to load permissions from storage', e);
            }
        }
    }

    has(feature: string, action: Action): boolean {
        return this.permissions()[feature.toUpperCase()]?.has(action) ?? false;
    }

    hasAny(feature: string, actions: Action[]): boolean {
        return actions.some((a) => this.has(feature.toUpperCase(), a));
    }

    hasAll(feature: string, actions: Action[]): boolean {
        return actions.every((a) => this.has(feature.toUpperCase(), a));
    }
}
